import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { behavioralAPI } from '../lib/api';
import { toast } from 'sonner';
import { ArrowLeft, Download, MessageSquareQuote, Lightbulb, AlertTriangle, Target } from 'lucide-react';
import { Button } from '../components/ui/button';
import { BarGraph, DivergingGraph, TRAIT_COLORS, TRAIT_NAMES } from '../components/behavioral/DiscGraphs';

const TERRA = '#0076A3';
const cardStyle = { background: '#fff', border: '1px solid #E7E5E4', borderRadius: 16, padding: '1.5rem' };
const sectionTitle = { fontFamily: 'Cabinet Grotesk', fontSize: '1.05rem', fontWeight: 700, color: '#002A38', display: 'flex', alignItems: 'center', gap: 8, marginBottom: '0.9rem' };

export default function BehavioralReportPage() {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const res = await behavioralAPI.getSession(sessionId);
      setData(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Report not found');
      navigate('/behavioral-assessments');
    } finally {
      setLoading(false);
    }
  }, [sessionId, navigate]);

  useEffect(() => { load(); }, [load]);

  if (loading) return <div style={{ padding: '3rem', textAlign: 'center', color: '#78716C' }}>Loading report…</div>;
  if (!data) return null;

  const profile = data.profile || {};
  const rec = profile.recruiter || {};
  const traits = profile.traits || {};

  const downloadPdf = () => window.open(behavioralAPI.reportPdfUrl(sessionId), '_blank');

  return (
    <div data-testid="behavioral-report-page">
      <button onClick={() => navigate('/behavioral-assessments')} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: 'none', border: 'none', color: '#78716C', fontSize: '0.85rem', cursor: 'pointer', marginBottom: '1rem' }}>
        <ArrowLeft size={16} /> Back to assessments
      </button>

      {/* Header */}
      <div style={{ ...cardStyle, marginBottom: '1.25rem', display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap' }}>
        <div>
          <h1 style={{ fontFamily: 'Cabinet Grotesk', fontSize: '1.5rem', fontWeight: 700, color: '#002A38' }}>{data.candidate_name}</h1>
          <p style={{ color: '#78716C', fontSize: '0.85rem', marginTop: 2 }}>
            {data.candidate_email}{data.job_title ? ` · ${data.job_title}` : ''}{data.completed_at ? ` · Completed ${new Date(data.completed_at).toLocaleDateString()}` : ''}
          </p>
          {profile.style_label && (
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, marginTop: 12, background: '#FFF3EC', color: TERRA, borderRadius: 999, padding: '6px 16px', fontWeight: 700, fontSize: '0.9rem' }}>
              <span style={{ width: 12, height: 12, borderRadius: 3, background: TRAIT_COLORS[profile.primary_style] }} />
              {profile.style_label}
            </div>
          )}
        </div>
        <Button data-testid="behavioral-download-pdf" onClick={downloadPdf} style={{ background: '#002A38', color: '#fff' }}>
          <Download size={16} style={{ marginRight: 6 }} /> Download PDF
        </Button>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1fr) minmax(0,1fr)', gap: '1.25rem', alignItems: 'start' }} className="behavioral-report-grid">
        {/* Graphs */}
        <div style={cardStyle}>
          <div style={sectionTitle}><Target size={18} color={TERRA} /> Psychometric graphs</div>
          <BarGraph title="Graph I — Public Self" subtitle="How they present under normal conditions (Most)" data={profile.graph1} testid="report-graph1" />
          <BarGraph title="Graph II — Private Self" subtitle="Their instinctive core (Least)" data={profile.graph2} testid="report-graph2" />
          <DivergingGraph title="Graph III — Adapted Style" subtitle="How much they shift to meet perceived expectations" data={profile.graph3} testid="report-graph3" />
        </div>

        {/* Trait breakdown */}
        <div style={cardStyle}>
          <div style={sectionTitle}><Lightbulb size={18} color={TERRA} /> Trait breakdown</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {['D', 'I', 'S', 'C'].map((t) => {
              const tr = traits[t] || {};
              return (
                <div key={t} data-testid={`report-trait-${t}`} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '0.6rem 0.75rem', border: '1px solid #F1EDE8', borderRadius: 10 }}>
                  <span style={{ width: 34, height: 34, borderRadius: 8, background: TRAIT_COLORS[t], color: '#fff', fontWeight: 800, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>{t}</span>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 700, color: '#002A38', fontSize: '0.88rem' }}>{TRAIT_NAMES[t]} <span style={{ fontWeight: 500, color: '#A8A29E', fontSize: '0.75rem' }}>· {tr.core_drive}</span></div>
                  </div>
                  <span style={{ fontSize: '0.72rem', fontWeight: 700, padding: '3px 10px', borderRadius: 999, background: '#EDF4F7', color: '#57534E' }}>{tr.level} · {Math.round(tr.score ?? 0)}</span>
                </div>
              );
            })}
          </div>

          <div style={{ marginTop: '1.25rem' }}>
            <h3 style={{ fontFamily: 'Cabinet Grotesk', fontWeight: 700, color: '#002A38', fontSize: '0.95rem', marginBottom: 6 }}>Summary</h3>
            <p style={{ fontSize: '0.85rem', color: '#57534E', lineHeight: 1.65 }}>{rec.summary}</p>
            {rec.blend_insight && (
              <div style={{ marginTop: 10, padding: '0.75rem 0.9rem', background: 'rgba(245,158,11,0.08)', border: '1px solid rgba(245,158,11,0.25)', borderRadius: 10, fontSize: '0.82rem', color: '#B45309', lineHeight: 1.6, display: 'flex', gap: 8 }}>
                <AlertTriangle size={16} style={{ flexShrink: 0, marginTop: 2 }} />
                <span><strong>Coaching note:</strong> {rec.blend_insight}</span>
              </div>
            )}
          </div>
        </div>

        {/* Strengths + watch-outs */}
        <div style={cardStyle}>
          <div style={sectionTitle}><Lightbulb size={18} color="#7FB08A" /> Strengths &amp; watch-outs</div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 14 }}>
            <div>
              <div style={{ fontSize: '0.72rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: '#7FB08A', marginBottom: 8 }}>Strengths</div>
              <ul style={listReset}>{(rec.strengths || []).map((x) => <li key={x} style={liStyle('#7FB08A')}><span style={dot('#7FB08A')} />{x}</li>)}</ul>
            </div>
            <div>
              <div style={{ fontSize: '0.72rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: '#E39A3B', marginBottom: 8 }}>Watch-outs</div>
              <ul style={listReset}>{(rec.watch_outs || []).map((x) => <li key={x} style={liStyle('#E39A3B')}><span style={dot('#E39A3B')} />{x}</li>)}</ul>
            </div>
            <div>
              <div style={{ fontSize: '0.72rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: '#7E8FB5', marginBottom: 8 }}>Ideal environment</div>
              <p style={{ fontSize: '0.85rem', color: '#57534E', lineHeight: 1.6, margin: 0 }}>{rec.ideal_environment}</p>
            </div>
          </div>
        </div>

        {/* Interview probes */}
        <div style={cardStyle}>
          <div style={sectionTitle}><MessageSquareQuote size={18} color={TERRA} /> Suggested interview probes</div>
          <p style={{ fontSize: '0.8rem', color: '#78716C', marginBottom: 12 }}>Tailored to this candidate's likely blind spots — use them to test for flexibility.</p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {(rec.interview_probes || []).map((p, i) => (
              <div key={p.question || i} data-testid={`report-probe-${i}`} style={{ padding: '0.9rem 1rem', background: '#EDF4F7', borderRadius: 12, borderLeft: `3px solid ${TERRA}` }}>
                <div style={{ fontSize: '0.68rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: TERRA, marginBottom: 4 }}>{p.focus}</div>
                <div style={{ fontSize: '0.88rem', color: '#002A38', lineHeight: 1.5 }}>{p.question}</div>
              </div>
            ))}
          </div>
          <div style={{ marginTop: 14 }}>
            <div style={{ fontSize: '0.72rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: '#78716C', marginBottom: 8 }}>How to communicate with them</div>
            <ul style={listReset}>{(rec.communication_tips || []).map((x) => <li key={x} style={liStyle('#7E8FB5')}><span style={dot('#7E8FB5')} />{x}</li>)}</ul>
          </div>
        </div>
      </div>

      <style>{`@media (max-width: 860px){ .behavioral-report-grid{ grid-template-columns: minmax(0,1fr) !important; } }`}</style>
    </div>
  );
}

const listReset = { listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: 7 };
const liStyle = () => ({ display: 'flex', gap: 9, fontSize: '0.85rem', color: '#44403C', lineHeight: 1.5 });
const dot = (c) => ({ width: 7, height: 7, borderRadius: 999, background: c, marginTop: 7, flexShrink: 0 });
